// ── Status chips: фильтр по статусу со счётчиками (вместо плиток StatusCards) ────────────────
/**
 * Плитки читались как статистика, а не как фильтр (замечание 16.10.2026) — теперь это ряд чипов
 * «Все · В работе · На согласовании · Согласованные · Отклонённые · Архив». «Архив» — отменённые:
 * во «Все» их нет (05.11.2026). Счётчики считает родитель по тому же набору, что видит список
 * (на /processes — вкладка + поиск + тип), поэтому число на чипе = числу строк под ним. Чип с нулём
 * неактивен, кроме выбранного; повторный клик по выбранному возвращает «Все». На узком окне (< 820)
 * — один ряд с горизонтальной прокруткой.
 */
import { ScrollView } from 'react-native'
import { Text, XStack } from 'tamagui'

import { useApp } from '../../../i18n'
import { useResponsive } from '../../../lib/responsive'
import { tone } from '../../../theme/tones'
import { Dot } from '../../../ui'
import { pressable } from '../../../ui/primitives'
import { PROCESS_STATUS } from '../registry'
import type { Filter } from './ProcessListWithFilters'

/** Порядок и подписи чипов; подпись чипа нужна и тегу активного фильтра на /processes. */
export const STATUS_CHIPS: { key: Filter; labelKey: string }[] = [
  { key: 'all', labelKey: 'doc.tabAll' },
  { key: 'work', labelKey: 'doc.fWork' },
  { key: 'review', labelKey: 'doc.fReview' },
  { key: 'done', labelKey: 'doc.fDone' },
  { key: 'rejected', labelKey: 'doc.fRejected' },
  { key: 'cancelled', labelKey: 'doc.fCancelled' },
]

export function StatusChips({
  counts,
  total,
  value,
  onChange,
  loading = false,
}: {
  counts: Record<string, number>
  /** Счётчик «Все» — размер набора до фильтра статуса. */
  total: number
  value: Filter
  onChange: (f: Filter) => void
  /** Первая загрузка — без счётчиков и без «неактивных» нулей. */
  loading?: boolean
}) {
  const { l } = useApp()
  const { width } = useResponsive()
  const narrow = width < 820

  const chips = STATUS_CHIPS.map(({ key, labelKey }) => {
    const n = key === 'all' ? total : counts[key] ?? 0
    const on = value === key
    const tn = key === 'all' ? undefined : tone(PROCESS_STATUS[key].tone)
    const off = !loading && n === 0 && !on
    const fg = on ? (tn ? tn.text : '$onPrimary') : '$color'
    return (
      <XStack
        key={key}
        {...pressable}
        // onPress снимаем сами: под RNTL disabled у Tamagui нажатие не глушит — не полагаемся на него.
        onPress={off ? undefined : () => onChange(on ? 'all' : key)}
        disabled={off}
        aria-pressed={on}
        cursor={off ? 'default' : 'pointer'}
        animation="quick"
        flexShrink={0}
        minHeight={narrow ? 44 : 34}
        alignItems="center"
        gap={7}
        paddingHorizontal={13}
        borderRadius={999}
        borderWidth={1}
        backgroundColor={on ? (tn ? tn.soft : '$primary') : '$background'}
        borderColor={on ? (tn ? tn.solid : '$primary') : '$borderColor'}
        opacity={off ? 0.45 : 1}
        hoverStyle={off ? {} : { borderColor: tn ? tn.solid : '$primary' }}
        pressStyle={off ? {} : { scale: 0.97 }}
      >
        {key !== 'all' ? <Dot tone={PROCESS_STATUS[key].tone} size={8} /> : null}
        <Text fontSize={13} fontWeight="600" color={fg} numberOfLines={1}>
          {l(labelKey)}
        </Text>
        {loading ? null : (
          <Text fontSize={12} fontWeight="700" color={on ? fg : '$muted'}>
            {n}
          </Text>
        )}
      </XStack>
    )
  })

  return narrow ? (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} role="group" aria-label={l('proc.status')}>
      <XStack gap={8}>{chips}</XStack>
    </ScrollView>
  ) : (
    <XStack role="group" aria-label={l('proc.status')} flexWrap="wrap" gap={8}>
      {chips}
    </XStack>
  )
}
